// /sms — the public description of the HoopHero Team Updates SMS program.
//
// Carrier (A2P 10DLC) reviewers must be able to see how parents opt in
// without an account or a team code. The real consent box only appears on the
// registration form after a team code is entered, so this page shows that same
// box (SmsConsent) as an example, with the program terms around it. It is
// shown whether or not FEATURE_SMS is on. Keep it in step with Terms §12A,
// Privacy §8a and server/controllers/smsController.js (STOP/HELP replies).

import React, { useState } from 'react';
import LegalLayout, { Section, P, UL, LI, Strong } from './LegalLayout';
import PageMeta from '../components/PageMeta';
import SmsConsent from '../components/SmsConsent';

const link = { color: '#C2460B' };

const ConsentExample = () => {
  const [checked, setChecked] = useState(false);
  return (
    <figure style={{ margin: '16px 0 8px', padding: '18px', background: '#fff', border: '1px solid #E2E5EC', borderRadius: '10px' }}>
      <label htmlFor="sms-example-phone" style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#0D1117', marginBottom: '6px' }}>Mobile phone (example)</label>
      <input id="sms-example-phone" type="tel" placeholder="(555) 555-0123" disabled style={{ width: '100%', maxWidth: '280px', padding: '9px 11px', border: '1px solid #D5D9E2', borderRadius: '8px', fontSize: '14px', marginBottom: '12px', background: '#F6F7FA' }} />
      <SmsConsent checked={checked} onChange={setChecked} />
      <figcaption style={{ marginTop: '12px', fontSize: '12px', color: '#9AA0B0' }}>
        Example of the box on the parent registration form. It starts unchecked; ticking it here does nothing.
      </figcaption>
    </figure>
  );
};

const SmsProgram = () => (
  <LegalLayout title="Text Message Program" lastUpdated="October 1, 2026">
    <PageMeta
      title="Text Message Program | HoopHero"
      description="How HoopHero Team Updates text messages work: who gets them, how parents opt in, message frequency, rates, STOP and HELP."
      path="/sms"
    />

    <Section heading="Program">
      <P>
        <Strong>HoopHero Team Updates</Strong> is the text message (SMS) program of
        HoopHero (www.hoophero.net), a product of Clarity Growth Pro LLC. Parents of
        youth basketball players who opt in receive game reminders, practice and
        schedule changes, payment reminders, and messages from their child&rsquo;s
        coach, and can reply to the coach by text.
      </P>
    </Section>

    <Section heading="How parents opt in">
      <P>
        A parent opts in by ticking the &quot;Text me team updates&quot; box when
        registering a child at www.hoophero.net/parent/register (or on the same form
        when a coach runs sign-up in person). The box is never pre-checked, and the
        consent text appears right next to it:
      </P>
      <ConsentExample />
      <P>
        Parents can also turn texts on or off at any time under Profile, Text
        messages. HoopHero stores the phone number, the opt-in choice and the date
        and time of consent with the parent&rsquo;s account.
      </P>
      <P>
        Texting is optional. Consent is not a condition of registering a child or of
        any purchase, and everything in the texts is also available in the app.
      </P>
    </Section>

    <Section heading="What you will receive">
      <UL>
        <LI>Game and practice reminders, schedule changes and cancellations.</LI>
        <LI>Payment reminders for fees set by your coach (HoopHero never takes payments by text).</LI>
        <LI>Messages from your coach, which you can answer by replying to the text.</LI>
      </UL>
      <P>
        Example: &quot;Lakeside Hoops (Coach Dana): Game reminder - Saturday 9:00 AM
        vs Eagles at Central YMCA, Court 2. Reply here or open
        https://www.hoophero.net/parent/messages. Reply STOP to opt out.&quot;
      </P>
    </Section>

    <Section heading="Frequency, cost, STOP and HELP">
      <UL>
        <LI><Strong>Message frequency varies</Strong> with your team&rsquo;s activity, typically a few messages a week during the season.</LI>
        <LI><Strong>Message and data rates may apply.</Strong> HoopHero never charges you for texts.</LI>
        <LI><Strong>To stop:</Strong> reply STOP to any message. You will get one confirmation and no further texts. Reply START to resume.</LI>
        <LI><Strong>For help:</Strong> reply HELP to any message, or email ruben@claritygrowthpro.com.</LI>
        <LI>Carriers are not liable for delayed or undelivered messages.</LI>
      </UL>
    </Section>

    <Section heading="Privacy">
      <P>
        Mobile numbers and consent records are never sold, and mobile information is
        never shared with third parties or affiliates for marketing or promotional
        purposes. Texts are sent through our messaging provider, Twilio. See
        our <a href="/privacy" style={link}>Privacy Policy</a> and section 12A of
        our <a href="/terms" style={link}>Terms of Service</a>.
      </P>
    </Section>
  </LegalLayout>
);

export default SmsProgram;
